import React, { useEffect, useState } from 'react';
import { AlarmClock } from 'lucide-react';
import { usePerfil } from '../../context/PerfilContext';
import { usePlus } from '../../context/PlusContext';
import { EtiquetaPlus } from '../informes/comun';
import { esApp } from '../../utils/plataforma';
import {
  AjustesRecordatorios, DIAS_INACTIVIDAD, leerAjustes, guardarAjustes, permisoRecordatorios,
} from '../../utils/recordatoriosGastos';
import { recordarAjusteDispositivo } from '../../utils/preferencias';

const Interruptor: React.FC<{ activo: boolean; onClick: () => void; disabled: boolean }> = ({ activo, onClick, disabled }) => (
  <button role="switch" aria-checked={activo} onClick={onClick} disabled={disabled}
    className={`relative w-12 h-7 rounded-full transition shrink-0 disabled:opacity-50 ${
      activo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
      activo ? 'translate-x-5' : ''}`} />
  </button>
);

/**
 * Recordatorios para apuntar gastos (solo en la app y con Finanzas). Vienen
 * apagados; el permiso de iOS se pide al encender uno, no al abrir la app.
 */
export const RecordatoriosGastosAjustes: React.FC = () => {
  const { tieneModulo, esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();
  const [ajustes, setAjustes] = useState<AjustesRecordatorios | null>(null);
  const [denegado, setDenegado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!esApp) return;
    leerAjustes().then(setAjustes).catch(() => {});
    permisoRecordatorios().then(p => setDenegado(p === 'denegado')).catch(() => {});
  }, []);

  if (!esApp || !ajustes || !tieneModulo('finanzas')) return null;

  const cambiar = async (cambios: Partial<AjustesRecordatorios>) => {
    const nuevos = { ...ajustes, ...cambios };
    setOcupado(true);
    try {
      if (await guardarAjustes(nuevos)) {
        setAjustes(nuevos); setDenegado(false);
        recordarAjusteDispositivo({ recordatoriosGastos: nuevos });
      }
      else setDenegado(true);
    } finally {
      setOcupado(false);
    }
  };

  const select = 'text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-100 px-2 py-1';

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-5 space-y-4">
      <div className="flex items-center gap-3">
        <AlarmClock className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Avisarme si llevo días sin apuntar</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Un solo aviso si no entras en la app. Si entras antes, no llega.
          </p>
        </div>
        <Interruptor activo={ajustes.inactividad} disabled={ocupado}
          onClick={() => cambiar({ inactividad: !ajustes.inactividad })} />
      </div>
      {ajustes.inactividad && (
        <label className="flex items-center gap-2 pl-8 text-xs text-gray-500 dark:text-gray-400">
          Tras
          <select value={ajustes.dias} disabled={ocupado} className={select}
            onChange={e => cambiar({ dias: Number(e.target.value) })}>
            {DIAS_INACTIVIDAD.map(d => <option key={d} value={d}>{d} días</option>)}
          </select>
          sin entrar
        </label>
      )}

      <div className="flex items-center gap-3">
        <div className="w-5 shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Recordatorio diario</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Cada día a la hora que elijas. Si ese día ya has apuntado algo, no sale.
          </p>
        </div>
        <Interruptor activo={ajustes.diario} disabled={ocupado}
          onClick={() => cambiar({ diario: !ajustes.diario })} />
      </div>
      {ajustes.diario && (
        <label className="flex items-center gap-2 pl-8 text-xs text-gray-500 dark:text-gray-400">
          A las
          <input type="time" value={ajustes.hora} disabled={ocupado} className={select}
            onChange={e => e.target.value && cambiar({ hora: e.target.value })} />
        </label>
      )}

      <div className="flex items-center gap-3">
        <div className="w-5 shrink-0" />
        <div className="flex-1">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Resumen del mes {!esPlus && <EtiquetaPlus />}</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            El día 1, un aviso para ver cuánto ahorraste el mes anterior y qué se disparó.
          </p>
        </div>
        <Interruptor activo={ajustes.resumenMes && esPlus} disabled={ocupado}
          onClick={() => (esPlus ? cambiar({ resumenMes: !ajustes.resumenMes }) : ofrecerPlus('informesPlus'))} />
      </div>

      {denegado && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Las notificaciones están bloqueadas para esta app. Actívalas en Ajustes de iOS → Notificaciones → myarinn.
        </p>
      )}
    </div>
  );
};
